<template>
  <b-overlay
    :show="show"
    spinner-variant="primary"
    spinner-type="grow"
    spinner-small
    rounded="sm"
  >
    <b-card :title="$t('pamm_new_account_create')">
      <p>{{ $t('pamm_account_create_description') }}</p>
      <p>{{ $t('pamm_account_create_description1') }}</p>
      <p class="mb-0">
        {{ $t('pamm_account_create_description2') }}
      </p>
    </b-card>

    <form-wizard
      :color="getColor('primary')"
      :title="null"
      :subtitle="null"
      shape="square"
      :next-button-text="$t('next')"
      :back-button-text="$t('back')"
      :finish-button-text="$t('complete_request')"
      class="mb-3"
      @on-complete="completeModalShow = true"
    >
      <tab-content
        :title="$t('pamm_account_step')"
        :before-change="validateAccountStep"
      >
        <validation-observer
          ref="accountRules"
          tag="form"
        >
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <h5 class="mb-0">
                {{ $t('pamm_account_step') }}
              </h5>
              <small class="text-muted">{{ $t('pamm_account_step_description') }}</small>
            </b-col>
            <b-col
              cols="12"
              sm="6"
            >
              <b-form-group
                :label="$t('account')"
                label-for="platform_account"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="$t('account')"
                  rules="required"
                >
                  <v-select
                    id="platform_account"
                    v-model="account.platform_account"
                    :options="accounts"
                    :reduce="item => item.account"
                    :clearable="false"
                    label="account"
                    :placeholder="$t('account')"
                  >
                    <template #option="option">
                      <div class="d-flex justify-content-between">
                        <span>{{ option.account }}</span>
                        <small class="text-muted">{{ option.group }} | 1:{{ option.leverage }} | {{ formatValue(option.balance, '$') }}</small>
                      </div>
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              sm="6"
            >
              <b-form-group
                :label="$t('account_nickname')"
                label-for="account_nickname"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="$t('account_nickname')"
                  rules="required"
                >
                  <b-form-input
                    id="account_nickname"
                    v-model="account.nick_name"
                    name="account_nickname"
                    :placeholder="$t('account_nickname')"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              sm="6"
            >
              <b-form-group
                :label="$t('manager_initial_capital')"
                label-for="manager_initial_capital"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="$t('manager_initial_capital')"
                  rules="required"
                >
                  <b-form-input
                    id="manager_initial_capital"
                    v-model.number="account.initial_capital"
                    type="number"
                    name="manager_initial_capital"
                    :placeholder="$t('manager_initial_capital')"
                    autocomplete="nope"
                    readonly
                  />
                  <small class="text-muted d-block mt-25">{{ $t('manager_initial_capital_description') }}</small>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              v-for="field in limitFields"
              :key="field.key"
              cols="12"
              sm="6"
            >
              <b-form-group>
                <validation-provider
                  v-slot="{ errors }"
                  :name="$t(field.label)"
                  rules="required"
                >
                  <label :for="field.key">
                    <span>{{ $t(field.label) }}</span>
                    <feather-icon
                      :id="`popover-${field.key}`"
                      icon="HelpCircleIcon"
                      size="18"
                      style="margin: 0 4px 4px;"
                      href="#"
                      tabindex="0"
                    />
                    <b-popover
                      :target="`popover-${field.key}`"
                      variant="danger"
                      placement="top"
                      triggers="focus"
                    >
                      <span>{{ $t(`${field.label}_description`) }}</span>
                    </b-popover>
                  </label>
                  <b-form-input
                    :id="field.key"
                    v-model.number="account[field.key]"
                    type="number"
                    step="any"
                    min="0"
                    :name="field.key"
                    :placeholder="$t(field.label)"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center"
            >
              <b-form-group>
                <b-form-checkbox
                  id="active_deposit"
                  v-model="account.active_deposit"
                  switch
                >
                  {{ $t('pamm_active_deposit') }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>

      <tab-content
        :title="$t('pamm_period_step')"
        :before-change="validatePeriodStep"
      >
        <validation-observer
          ref="periodRules"
          tag="form"
        >
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <h5 class="mb-0">
                {{ $t('pamm_period_step') }}
              </h5>
              <small class="text-muted">{{ $t('pamm_period_step_description') }}</small>
            </b-col>
          </b-row>
          <period-form-fields v-model="period" />
          <b-row>
            <b-col
              class="text-danger"
              style="cursor: pointer;"
              cols="12"
              @click="contractModalShow = true"
            >
              {{ $t('show_contract_here') }}
            </b-col>
            <b-col
              cols="12"
              class="my-1"
            >
              <b-form-checkbox
                id="isAgree"
                v-model="isAgree"
                name="isAgree"
              >
                {{ $t('pamm_create_account_is_agree') }}
              </b-form-checkbox>
              <small
                v-if="!isAgree"
                class="text-danger"
              >
                {{ $t('pamm_agree_required') }}
              </small>
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>
    </form-wizard>

    <!-- Modal -->
    <b-modal
      v-model="completeModalShow"
      centered
      size="lg"
      :title="$t('identification')"
      no-close-on-backdrop
    >
      <h4 class="my-1">
        {{ $t('require_identification') }}
      </h4>
      <h6>
        {{ $t('require_identification_description') }}
      </h6>
      <b-alert
        show
        variant="primary"
        class="p-2"
      >
        <p
          v-for="(item, index) in $t('pamm_agreement_context')"
          :key="index"
        >
          {{ item }}
        </p>
      </b-alert>
      <b-img
        class="mx-auto d-flex justify-center"
        fluid
        width="160"
        :src="require('@/assets/images/image/3.webp')"
      />
      <b-form-file
        v-model="file"
        class="my-1"
        accept="image/png, image/jpg, image/jpeg"
        :placeholder="$t('no_file_chosen')"
      />
      <div>
        <h6 class="text-danger">
          {{ $t('pamm_agreement_notes.title') }}
        </h6>
        <div style="margin-bottom: 4px;">
          <span class="font-weight-bold">{{ $t('pamm_agreement_notes.note_one_title') }}</span>
          <span style="margin: 0 2px;">:</span>
          <span>{{ $t('pamm_agreement_notes.note_one_description') }}</span>
        </div>
        <div style="margin-bottom: 4px;">
          <span class="font-weight-bold">{{ $t('pamm_agreement_notes.note_two_title') }}</span>
          <span style="margin: 0 2px;">:</span>
          <span>{{ $t('pamm_agreement_notes.note_two_description') }}</span>
        </div>
        <div style="margin-bottom: 4px;">
          <span class="font-weight-bold">{{ $t('pamm_agreement_notes.note_three_title') }}</span>
          <span style="margin: 0 2px;">:</span>
          <span>{{ $t('pamm_agreement_notes.note_three_description') }}</span>
        </div>
      </div>
      <div class="my-2">
        <p>{{ $t('pamm_create_request_desc') }}</p>
      </div>
      <template #modal-footer>
        <b-button
          :disabled="!file || createPending"
          variant="primary"
          block
          @click="createAccount"
        >
          <span v-if="!createPending">
            {{ $t('request_create_account') }}
          </span>
          <b-spinner
            v-else
            small
            type="grow"
          />
        </b-button>
      </template>
    </b-modal>

    <b-modal
      v-model="contractModalShow"
      centered
      size="lg"
      :title="$t('contract')"
      hide-footer
    >
      <h1 style="font-size: 20px; font-weight: bold; text-align: center;">
        PAMM Account Opening Agreement
      </h1>
      <p>This agreement (the "Agreement") is made and entered into as of the [Date] by and between [Your Name], with an address of [Your Address] (the "Client"), and [Company Name], a company duly organized and existing under the laws of [Country] with its principal place of business at [Company Address] (the "Company").</p>
      <p style="font-style: italic;">
        <strong>WHEREAS,</strong> the Client desires to open a PAMM account with the Company; and
      </p>
      <p style="font-style: italic;">
        <strong>WHEREAS,</strong> the Company is willing to accept the Client as a PAMM account holder subject to the terms and conditions of this Agreement.
      </p>
      <h2 style="font-size: 18px; font-weight: bold;">
        NOW, THEREFORE, in consideration of the mutual covenants contained herein, the parties agree as follows:
      </h2>
      <h3><span style="font-weight: bold; text-decoration: underline;">1. Account Opening</span></h3>
      <p>1.1 The Client hereby applies to open a PAMM account with the Company.</p>
      <p>1.2 The Client shall provide the Company with all information and documentation required by the Company to open and maintain the PAMM account, including but not limited to:</p>
      <ul style="list-style-type: disc; padding-left: 20px;">
        <li>Full name and contact information</li>
        <li>Proof of identity</li>
        <li>Proof of address</li>
        <li>Source of funds</li>
        <li>Risk tolerance assessment</li>
      </ul>
      <p>1.3 The Company shall review the Client's application and supporting documentation and, at its sole discretion, approve or deny the application.</p>
      <h3><span style="font-weight: bold; text-decoration: underline;">2. PAMM Account</span></h3>
      <p>2.1 A PAMM account is a type of managed account where the Client's funds are pooled with the funds of other PAMM account holders and invested by a Money Manager.</p>
      <p>2.2 The Client understands and agrees that the Money Manager has sole discretion over the investment of the PAMM account funds.</p>
      <p>2.3 The Client acknowledges and agrees that the Money Manager's investment decisions may result in losses as well as gains.</p>
      <p>2.4 The Client shall not interfere with the Money Manager's investment decisions.</p>
      <h3><span style="font-weight: bold; text-decoration: underline;">3. Fees and Charges</span></h3>
      <p>3.1 The Company shall charge the Client a management fee for the PAMM account. The management fee shall be calculated as a percentage of the PAMM account's net asset value (NAV) on a monthly basis.</p>
      <p>3.2 The Company may also charge the Client other fees and charges, such as withdrawal fees and performance fees.</p>
      <p>3.3 The Client shall be responsible for all applicable taxes on the fees and charges.</p>
      <h3><span style="weight: bold; text-decoration: underline;">4. Risk Disclosure</span></h3>
      <p>4.1 The Client acknowledges and agrees that PAMM accounts are speculative investments and involve a high degree of risk.</p>
      <p>4.2 The Client understands and agrees that there is a possibility that the Client may lose all or a portion of the funds invested in the PAMM account.</p>
      <p>4.3 The Client should not invest in a PAMM account unless the Client can afford to lose the entire investment.</p>
      <h3><span style="font-weight: bold; text-decoration: underline;">5. Termination</span></h3>
      <p>5.1 Either party may terminate this Agreement at any time upon [Notice Period] written notice to the other party.</p>
      <p>5.2 Upon termination of this Agreement, the Client's funds shall be redeemed from the PAMM account and distributed to the Client within [Timeframe].</p>
      **Please note:** This is a basic implementation of inline styles. You can further customize the styles (font size, colors, etc.) based on your specific needs.
    </b-modal>
  </b-overlay>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BPopover,
  BModal,
  BAlert,
  BImg,
  BFormFile,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { alphanumeric } from '@/mixins/alphanumeric'
import PammApis from '@/modules/pamm/service/apis'
import MetatraderService from '@/modules/forex/service/apis'
import { numberWithCommas } from '@/utils/index'
import { emptyPeriod, isPeriodDateOrderValid } from '@/modules/pamm/utils/period'
import PeriodFormFields from '../components/period-form-fields.vue'

const pammApis = new PammApis()
const metatraderService = new MetatraderService()

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BPopover,
    BModal,
    BAlert,
    BImg,
    BFormFile,
    BButton,
    BSpinner,
    FormWizard,
    TabContent,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    PeriodFormFields,
  },
  directives: {
    Ripple,
  },
  mixins: [alphanumeric],
  data() {
    return {
      show: false,
      required,
      isAgree: true,
      createPending: false,
      completeModalShow: false,
      file: null,
      accounts: [],
      limitFields: [
        { key: 'minimum_invest_amount', label: 'minimum_investment' },
        { key: 'maximum_invest_amount', label: 'maximum_investment' },
      ],
      account: {
        platform_account: null,
        nick_name: '',
        initial_capital: null,
        minimum_invest_amount: null,
        maximum_invest_amount: null,
        active_deposit: true,
      },
      period: emptyPeriod(),
      contractModalShow: false,
    }
  },
  computed: {
    selectedAccount() {
      return this.accounts.find(item => item.account === this.account.platform_account) ?? null
    },
  },
  watch: {
    selectedAccount: 'fillInitialCapital',
  },
  mounted() {
    this.getAccounts()
  },
  methods: {
    async getAccounts() {
      this.show = true
      await metatraderService.getAccounts().then(res => {
        this.accounts = res.data.results ?? []
        if (this.accounts.length === 1) {
          this.account.platform_account = this.accounts[0].account
        }
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.show = false
      })
    },
    fillInitialCapital(selected) {
      const balance = Number(selected?.balance)
      if (selected && Number.isFinite(balance)) {
        this.account.initial_capital = balance
      }
    },
    formatValue(value, unit) {
      if (value === null || value === undefined) return '-'
      return `${numberWithCommas(value)} ${unit}`.trim()
    },
    validateAccountStep() {
      return this.$refs.accountRules.validate()
    },
    async validatePeriodStep() {
      const success = await this.$refs.periodRules.validate()
      return success && isPeriodDateOrderValid(this.period) && this.isAgree
    },
    async createAccount() {
      this.createPending = true
      await pammApis.createAccount({
        ...this.account,
        platform: this.selectedAccount?.platform,
        active_deposit: this.account.active_deposit ? 1 : 0,
        ...this.period,
        file: this.file,
      }).then(res => {
        this.completeModalShow = false
        this.$swal({
          title: res.data.message,
          icon: 'success',
          timer: 4000,
          showConfirmButton: false,
        })
        this.$router.push({ name: 'pamm_management_account' })
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.createPending = false
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';
</style>
